import {
  Anchor,
  Button,
  Container,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconCalculator,
  IconCheck,
  IconHandStop,
  IconHeartHandshake,
  IconLock,
  IconMessage,
  IconNumber1,
  IconNumber2,
  IconNumber3,
  IconToolsKitchen2,
  IconUsers,
  IconUsersGroup,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import {
  MARKETING_HOME_INCLUDED_ANCHORS,
  MARKETING_HOME_INCLUDED_IDS,
  MARKETING_HOME_STEP_IDS,
  MARKETING_TRUST_KEYS,
} from '#marketing/lib/marketing-capabilities.js';
import { localePath } from '#marketing/lib/marketing-locale.js';
import { useMarketingLocale } from '#marketing/lib/use-marketing-locale.js';
import { MarketingPillarCard } from './MarketingPillarCard.jsx';
import { MarketingSection } from './MarketingSection.jsx';

const pillarConfig = [
  {
    icon: IconUsers,
    titleKey: 'marketing.home.pillarAllInOneTitle',
    descriptionKey: 'marketing.home.pillarAllInOneDescription',
  },
  {
    icon: IconUsers,
    titleKey: 'marketing.home.pillarOnePlaceTitle',
    descriptionKey: 'marketing.home.pillarOnePlaceDescription',
  },
  {
    icon: IconLock,
    titleKey: 'marketing.home.pillarTrustTitle',
    descriptionKey: 'marketing.home.pillarTrustDescription',
  },
];

const STEP_ICONS = [IconNumber1, IconNumber2, IconNumber3];

const INCLUDED_ICONS = {
  hotLunch: IconToolsKitchen2,
  volunteers: IconHandStop,
  budget: IconCalculator,
  communications: IconMessage,
  meetings: IconUsersGroup,
  fundraising: IconHeartHandshake,
};

/**
 * Home page sections below the hero: pillars, how-it-works teaser, included, trust, CTA.
 */
export function HomeMarketingSections() {
  const { t } = useTranslation('common');
  const locale = useMarketingLocale();

  return (
    <>
      <Container size="lg" py="xl">
        <MarketingSection
          titleKey="marketing.home.pillarsTitle"
          descriptionKey="marketing.home.pillarsDescription"
        />
        <SimpleGrid cols={{ base: 1, md: 3 }} spacing="xl">
          {pillarConfig.map((pillar) => (
            <MarketingPillarCard key={pillar.titleKey} {...pillar} />
          ))}
        </SimpleGrid>
      </Container>

      <Container size="lg" pb="xl">
        <MarketingSection
          titleKey="marketing.home.howItWorksTeaserTitle"
          descriptionKey="marketing.home.howItWorksTeaserDescription"
        />
        <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg" mb="lg">
          {MARKETING_HOME_STEP_IDS.map((stepId, index) => {
            const StepIcon = STEP_ICONS[index] ?? IconNumber1;
            return (
              <Paper key={stepId} withBorder p="md" radius="md" bg="white">
                <Group wrap="nowrap" align="center" gap="sm" mb="xs">
                  <ThemeIcon color="pachubColourScheme" size={36} radius="xl">
                    <StepIcon size={18} />
                  </ThemeIcon>
                  <Text fw={600} lh="snug">
                    {t(`marketing.home.steps.${stepId}.title`)}
                  </Text>
                </Group>
                <Text size="sm" c="dimmed">
                  {t(`marketing.home.steps.${stepId}.description`)}
                </Text>
              </Paper>
            );
          })}
        </SimpleGrid>
        <Group justify="center">
          <Button
            component={Link}
            to={localePath(locale, '/how-it-works')}
            variant="light"
            color="pachubColourScheme"
          >
            {t('marketing.home.howItWorksTeaserCta')}
          </Button>
        </Group>
      </Container>

      <Container size="lg" pb="xl">
        <MarketingSection
          titleKey="marketing.home.includedTitle"
          descriptionKey="marketing.home.includedDescription"
        />
        <SimpleGrid cols={{ base: 2, sm: 3, lg: 6 }} spacing="md">
          {MARKETING_HOME_INCLUDED_IDS.map((itemId) => {
            const Icon = INCLUDED_ICONS[itemId] ?? IconUsers;
            const anchor = MARKETING_HOME_INCLUDED_ANCHORS[itemId];
            const href = `${localePath(locale, '/features')}#${anchor}`;

            return (
              <Anchor
                key={itemId}
                component={Link}
                to={href}
                underline="never"
                c="inherit"
              >
                <Paper withBorder p="md" radius="md" bg="white" ta="center">
                  <Stack gap="sm" align="center">
                    <ThemeIcon color="pachubColourScheme" size={40} radius="md" variant="light">
                      <Icon size={22} stroke={1.5} />
                    </ThemeIcon>
                    <Text fw={600} size="sm">
                      {t(`marketing.home.includedItems.${itemId}`)}
                    </Text>
                  </Stack>
                </Paper>
              </Anchor>
            );
          })}
        </SimpleGrid>
      </Container>

      <Container size="lg" pb="xl">
        <Title order={2} ta="center" mb="lg">
          {t('marketing.home.trustTitle')}
        </Title>
        <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
          {MARKETING_TRUST_KEYS.map((key) => (
            <Paper key={key} withBorder p="md" radius="md" bg="white">
              <Group wrap="nowrap" align="flex-start" gap="sm">
                <ThemeIcon color="pachubColourScheme" size={28} radius="xl">
                  <IconCheck size={14} />
                </ThemeIcon>
                <Text size="sm">{t(`marketing.trust.${key}`)}</Text>
              </Group>
            </Paper>
          ))}
        </SimpleGrid>
      </Container>

      <Container size="lg" pb="xl">
        <Paper
          withBorder
          shadow="sm"
          radius="md"
          p="xl"
          bg="pachubColourScheme.0"
          ta="center"
        >
          <Text fw={700} size="xl" mb="sm">
            {t('marketing.home.pilotCtaTitle')}
          </Text>
          <Text c="dimmed" maw={520} mx="auto" mb="lg">
            {t('marketing.home.pilotCtaDescription')}
          </Text>
          <Group justify="center">
            <Button component={Link} to={localePath(locale, '/contact')} size="md" color="pachubColourScheme">
              {t('marketing.home.pilotCtaButton')}
            </Button>
          </Group>
        </Paper>
      </Container>
    </>
  );
}

/**
 * Hero call-to-action buttons shared styling hook for home hero.
 */
export function MarketingHeroActions() {
  const { t } = useTranslation('common');
  const locale = useMarketingLocale();

  return (
    <Group mt="xl" gap="md">
      <Button component={Link} to={localePath(locale, '/features')} size="md" color="pachubColourScheme">
        {t('marketing.hero.primaryCta')}
      </Button>
      <Button component={Link} to={localePath(locale, '/contact')} size="md" variant="default">
        {t('marketing.hero.secondaryCta')}
      </Button>
    </Group>
  );
}
